import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { USERAXIOS } from "../../api";
import { JobActivity } from "../../types/resumes-managment/resume";

interface JobActivitiesResponse {
  data: JobActivity[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export const useJobActivities = () => {
  return useQuery({
    queryKey: ["jobActivities"],
    queryFn: async (): Promise<JobActivity[]> => {
      const response = await USERAXIOS.get<JobActivitiesResponse>(
        "/job_activities"
      );
      return response.data.data;
    },
  });
};

export const useJobActivitiesInfinite = (
  pageSize: number = 20,
  searchTerm: string = ""
) => {
  return useInfiniteQuery({
    queryKey: ["jobActivities", "infinite", pageSize, searchTerm],
    queryFn: async ({ pageParam = 1 }): Promise<JobActivitiesResponse> => {
      const response = await USERAXIOS.get<JobActivitiesResponse>(
        `/job_activities`,
        {
          params: {
            page: pageParam,
            per_page: pageSize,
            title: searchTerm || undefined,
          },
        }
      );
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined;
    },
    initialPageParam: 1,
  });
};

export const useJobActivitiesForAutocomplete = (
  pageSize: number = 20,
  searchTerm: string = ""
) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useJobActivitiesInfinite(pageSize, searchTerm);

  // Flatten all pages into a single array
  const jobActivities = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) || [];
  }, [data]);

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return {
    items: jobActivities,
    loading: isLoading || isFetchingNextPage,
    hasMore: hasNextPage,
    loadMore,
    error,
    refetch,
    totalPages: data?.pages[0]?.meta.last_page || 0,
    totalItems: data?.pages[0]?.meta.total || 0,
  };
};
